<script lang="ts">
import FindCompanySkills from "@/Components/FindCompany/FindCompanySkills.vue";
import GenericButton from "@/Components/Generics/GenericButton.vue";
import GenericModal from "@/Components/Generics/GenericModal.vue";
import Icon from "@/Components/Generics/Icon.vue";
import Plane from "@/Components/Generics/Plane.vue";
import TextUtilities from "@/Helpers/TextUtilities";
import { QualificationInput, QualificationsGroups } from "@/Types";
import { Company } from "@/Types/company";
import { defineComponent, PropType } from "vue";

export default defineComponent({
    components: {
        Plane,
        GenericModal,
        GenericButton,
        Icon,
        FindCompanySkills,
    },
    props: {
        company: {
            type: Object as PropType<Company>,
            required: true,
        },
        moreInfo: {
            type: Boolean,
            default: false,
        },

        qualificationGroups: {
            type: Array as PropType<QualificationsGroups>,
            required: true,
        },
        qualificationInput: {
            type: Array as PropType<QualificationInput>,
            required: true,
        },
    },
    emits: ["clickDetails"],
    data: () => ({
        modalOpenName: null as string,
    }),
    computed: {
        companyInfoBlocks(): Array<{ title: string; content: string }> {
            if (!this.company) return [];

            const infos = [];

            if (!!this.company.occupation) {
                infos.push({ title: this.$trans("company.occupation"), content: this.company.occupation });
            }
            if (!!this.company.internship_time) {
                infos.push({ title: this.$trans("company.internship_time"), content: this.company.internship_time });
            }
            if (!!this.company.internship_scope) {
                infos.push({ title: this.$trans("company.internship_scope"), content: this.company.internship_scope });
            }
            if (!!this.company.educational_requirements) {
                infos.push({ title: this.$trans("company.educational_requirements"), content: this.company.educational_requirements });
            }

            return infos;
        },
        companyInfoLeft(): Array<string> {
            if (!this.company) return [];

            const infos = [];

            if (!!this.company.street_name) {
                infos.push(`${this.company.street_name} ${this.company.street_number}`);
            }
            if (!!this.company.zipcode || this.company.city) {
                infos.push(`${this.company.zipcode} ${this.company.city}`);
            }
            if (!!this.company.country) {
                infos.push(this.company.country);
            }

            return infos;
        },
        companyInfoRight(): Array<{ info: string; icon: string }> {
            if (!this.company) return [];

            const infos = [];

            if (!!this.company.phone) {
                infos.push({ info: this.company.phone, icon: "djf_update_phone" });
            }
            if (!!this.company.email) {
                infos.push({ info: this.company.email, icon: "djf_update_mail" });
            }
            if (!!this.company.website) {
                infos.push({ info: this.company.website, icon: "djf_update_web" });
            }

            return infos;
        },

        companyExtrasText(): string {
            if (!this.company?.internship_extras) return "";

            return TextUtilities.formatBulletPoints(this.company.internship_extras);
        },
    },
    methods: {
        openModal(name: string): void {
            this.modalOpenName = name;
        },
        closeModal(): void {
            this.modalOpenName = null;
        },

        onClickDetails(): void {
            this.$emit("clickDetails");
        },
    },
});
</script>

<template>
    <div v-if="company" :id="`company-card-${company.id}`" class="flex flex--v flex--width flex--gap-30">
        <Plane class="company-card" :class="{ 'more-info': moreInfo }">
            <!-- RATING -->
            <div v-if="!company.withoutRating && !isNaN(company.rating)" class="company-right flex flex--valign-c flex--gap-20">
                <span class="company-right-rating-text text text--primary-b text--uppercase text--size-20 text--semi-bold text--right">
                    {{ $trans("company.rating") }}
                </span>

                <Plane class="company-right-plane" :primary="true">
                    <div class="flex flex--height flex--valign-c">
                        <span class="company-right-plane-text text text--color-white text--shadow text--size-40 text--semi-bold">
                            {{ `${(company.rating * 100).toFixed(2)}%` }}
                        </span>
                    </div>
                </Plane>
            </div>

            <!-- OCCUPATION -->
            <Plane v-if="company.occupation" class="company-occupation-plane company-left-plane company-left-plane--secondary" :secondary="true">
                <div class="flex flex--height flex--valign-c">
                    <span class="text text--color-white text--shadow text--size-22 text--semi-bold">
                        {{ company.occupation }}
                    </span>
                </div>
            </Plane>

            <!-- BODY -->
            <div class="company-body">
                <!-- LEFT SIDE -->
                <div class="company-left">
                    <!-- COMPANY LOGO -->
                    <Plane v-if="company.image" class="company-left-plane company-left-plane--logo">
                        <div class="flex flex--width flex--valign-c flex--halign-c">
                            <div class="square-logo flex flex--valign-c flex--halign-c">
                                <img :src="company.image.imageUrl" :alt="$trans('company.image_alt')" />
                            </div>
                        </div>
                    </Plane>

                    <!-- COMPANY NAME -->
                    <Plane class="company-left-plane company-left-plane--primary" :primary="true">
                        <div class="flex flex--height flex--valign-c">
                            <span class="text text--color-white text--shadow text--size-24 text--semi-bold">
                                {{ company.name }}
                            </span>
                        </div>
                    </Plane>
                </div>

                <div v-if="!company.withoutRating && !isNaN(company.rating)" class="company-rating-mobile flex flex--valign-c flex--halign-sb">
                    <span class="text text--primary-b text--uppercase text--size-20 text--semi-bold text--left">
                        {{ $trans("company.rating") }}
                    </span>

                    <Plane class="company-rating-mobile-plane" :primary="true">
                        <div class="flex flex--height flex--valign-c">
                            <span class="company-rating-mobile-plane-text text text--color-white text--shadow text--size-40 text--semi-bold">
                                {{ `${(company.rating * 100).toFixed(2)}%` }}
                            </span>
                        </div>
                    </Plane>
                </div>

                <!-- CONTENT -->
                <div class="company-card-content">
                    <div class="economic-sectors flex flex--wrap flex--gap-15">
                        <div v-for="sector in company.sectors" :key="sector.id" class="economic-sector text text--primary-b text--uppercase text--size-20 text--bold" @click="openModal(sector.name)">
                            {{ $trans(`company.${sector.name}`) }}
                        </div>
                    </div>

                    <span v-if="company.description" class="company-description text text--accent-gray-b text--size-18 text--height-24 text--medium" :class="{ 'text--line-clamp-3': !moreInfo }">
                        {{ company.description }}
                    </span>
                </div>
            </div>

            <GenericButton v-if="!moreInfo" class="more-info-button" :title="$trans('company.button_more_info')" :circular="true" @click="onClickDetails" />
        </Plane>

        <!-- DETAIL VIEW ONLY -->
        <template v-if="moreInfo">
            <!-- QUALIFICATIONS / REQUIREMENTS -->
            <FindCompanySkills :qualification-groups="qualificationGroups" :qualification-input="qualificationInput" :company-requirements="company.requirements" :is-company="true" />

            <!-- COMPANY INFO -->
            <Plane class="company-info">
                <div class="flex flex--width flex--v flex--valign-c flex--gap-30">
                    <div class="company-info-plane-wrapper flex flex--width flex--wrap flex--gap-30 flex--halign-c">
                        <Plane v-for="info in companyInfoBlocks" :key="info.title" class="company-info-plane">
                            <div class="flex flex--v flex--valign-c flex--gap-15">
                                <span class="text text--uppercase text--primary-b text--size-20 text--medium text--center text--less-gap">
                                    {{ info.title }}
                                </span>
                                <span class="text text--accent-gray-b text--size-18 text--regular text--center text--height-24">
                                    {{ info.content }}
                                </span>
                            </div>
                        </Plane>
                    </div>

                    <div v-if="!company.internship_extras && !company.contact" class="company-info-bottom flex flex--width flex--halign-sb flex--valign-e flex--gap-30">
                        <div class="company-info-bottom-left flex flex--v flex--gap-10 flex--valign-s">
                            <span class="company-info-bottom-left-text text text--uppercase text--primary-b text--size-20 text--medium text--left text--less-gap">
                                {{ company.name }}
                            </span>

                            <span v-for="info in companyInfoLeft" :key="info" class="company-info-bottom-left-text text text--accent-gray-b text--size-20 text--medium text--left text--less-gap">
                                {{ info }}
                            </span>
                        </div>

                        <div v-if="companyInfoRight.length > 0" class="company-info-bottom-right flex flex--v flex--gap-10 flex--valign-e">
                            <div v-for="info in companyInfoRight" :key="info.info" class="company-info-bottom-right-content flex flex--valign-c flex--gap-10">
                                <Icon class="company-info-bottom-right-content-icon" :identifier="info.icon" />
                                <span class="company-info-bottom-right-content-text text text--accent-gray-b text--size-20 text--medium text--right text--less-gap">
                                    {{ info.info }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </Plane>

            <Plane v-if="!!company.internship_extras" class="company-extras">
                <div class="flex flex--v flex--valign-c flex--gap-30">
                    <Plane class="company-extras-plane">
                        <div class="flex flex--v flex--valign-c flex--gap-15">
                            <span class="text text--uppercase text--primary-b text--size-20 text--medium text--center text--less-gap">
                                {{ $trans("company.internship_extras") }}
                            </span>
                            <span class="company-extras-plane-text text text--accent-gray-b text--size-18 text--regular text--center text--height-24">
                                {{ companyExtrasText }}
                            </span>
                        </div>
                    </Plane>

                    <div v-if="!company.contact" class="company-info-bottom flex flex--width flex--halign-sb flex--valign-e flex--gap-30">
                        <div class="company-info-bottom-left flex flex--v flex--gap-10 flex--valign-s">
                            <span class="company-info-bottom-left-text text text--uppercase text--primary-b text--size-20 text--medium text--left text--less-gap">
                                {{ company.name }}
                            </span>

                            <span v-for="info in companyInfoLeft" :key="info" class="company-info-bottom-left-text text text--accent-gray-b text--size-20 text--medium text--left text--less-gap">
                                {{ info }}
                            </span>
                        </div>

                        <div v-if="companyInfoRight.length > 0" class="company-info-bottom-right flex flex--v flex--gap-10 flex--valign-e">
                            <div v-for="info in companyInfoRight" :key="info.info" class="company-info-bottom-right-content flex flex--valign-c flex--gap-10">
                                <Icon class="company-info-bottom-right-content-icon" :identifier="info.icon" />
                                <span class="company-info-bottom-right-content-text text text--accent-gray-b text--size-20 text--medium text--right text--less-gap">
                                    {{ info.info }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </Plane>

            <Plane v-if="company.contact" class="company-contact">
                <div class="flex flex--width flex--v flex--valign-c flex--gap-30">
                    <div class="company-contact-info-wrapper flex flex--width flex--valign-c flex--halign-c flex--gap-30">
                        <Plane v-if="company.image_contact" class="company-contact-image">
                            <img :src="company.image_contact.imageUrl" :alt="$trans('company.image_contact_alt')" />
                        </Plane>

                        <Plane class="company-contact-info">
                            <div class="flex--extend flex flex--v flex--valign-c flex--halign-c flex--gap-30">
                                <span class="text text--uppercase text--primary-b text--size-20 text--medium text--center text--less-gap">
                                    {{ $trans("company.contact") }}
                                </span>
                                <span class="text text--accent-gray-b text--size-18 text--regular text--center text--height-24">
                                    {{ company.contact }}
                                </span>
                            </div>
                        </Plane>
                    </div>

                    <div class="company-info-bottom flex flex--width flex--halign-sb flex--valign-e flex--gap-30">
                        <div class="company-info-bottom-left flex flex--v flex--gap-10 flex--valign-s">
                            <span class="company-info-bottom-left-text text text--uppercase text--primary-b text--size-20 text--medium text--left text--less-gap">
                                {{ company.name }}
                            </span>

                            <span v-for="info in companyInfoLeft" :key="info" class="company-info-bottom-left-text text text--accent-gray-b text--size-20 text--medium text--left text--less-gap">
                                {{ info }}
                            </span>
                        </div>

                        <div v-if="companyInfoRight.length > 0" class="company-info-bottom-right flex flex--v flex--gap-10 flex--valign-e">
                            <div v-for="info in companyInfoRight" :key="info.info" class="company-info-bottom-right-content flex flex--valign-c flex--gap-10">
                                <Icon class="company-info-bottom-right-content-icon" :identifier="info.icon" />
                                <span class="company-info-bottom-right-content-text text text--accent-gray-b text--size-20 text--medium text--right text--less-gap">
                                    {{ info.info }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </Plane>
        </template>
    </div>

    <GenericModal v-if="!!modalOpenName" @closed="closeModal">
        <template #title>{{ $trans(`company.${modalOpenName}`) }}</template>
        <template #content>
            <div class="flex flex--v flex--valign-c text text--size-18 text--height-24 text--primary-b text--medium text--center">{{ $trans(`company.${modalOpenName}_desc`) }}</div>
        </template>
    </GenericModal>
</template>

<style lang="scss" scoped>
.more-info-button {
    position: absolute;
    left: 50%;
    bottom: rem(-40);
    transform: translateX(-50%);
    padding-left: rem(70);
    padding-right: rem(70);
}

.company-contact {
    .company-contact-info-wrapper {
        .company-contact-image {
            aspect-ratio: 1 / 1;
            width: unset;
            max-width: min(30%, #{rem(300)});
            background-color: $color-accent-light-b;

            img {
                width: 100%;
                height: 100%;
                object-fit: contain;
            }
        }

        .company-contact-info {
            width: unset;
            max-width: min(30%, #{rem(300)});
            background-color: #fff;
        }
    }
}

.company-info {
    .company-info-plane-wrapper {
        .company-info-plane {
            background-color: #fff;
            border-radius: rem(10);
            max-width: calc(33% - #{rem(20)});
            width: 100%;
        }
    }
}

.company-info-bottom {
    .company-info-bottom-right {
        .company-info-bottom-right-content {
            .company-info-bottom-right-content-icon {
                width: rem(24);
                height: rem(24);
                margin-top: rem(-1);
            }
        }
    }
}

.company-extras {
    .company-extras-plane {
        background-color: #fff;
        border-radius: rem(10);
        width: fit-content;

        .company-extras-plane-text {
            white-space: pre-line;
        }
    }
}

.company-card {
    &.more-info {
        padding-bottom: rem(10);

        .square-logo {
            width: rem(256);
            height: rem(256);
        }
    }

    .square-logo {
        width: rem(128);
        height: rem(128);
        aspect-ratio: 1 / 1;

        img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;
        }
    }

    .company-right {
        position: absolute;
        top: 0;
        right: rem(-40);
        max-width: max-content;

        .company-right-rating-text {
            text-wrap: nowrap;
        }

        .company-right-plane {
            width: auto;
            padding: 0 rem(18);
            border-radius: rem(9);
            height: rem(65);
            box-shadow: 0px rem(3) 0px 0px #00000028;

            &::before {
                content: "";
                position: absolute;
                top: rem(-10);
                right: 0;
                width: rem(15);
                height: 100%;
                z-index: -1;
                background-color: $color-primary-b-darker;
                clip-path: polygon(0% 0%, calc(100% - #{rem(1)}) #{rem(15)}, 0% #{rem(15)});
            }
        }
    }

    .company-left-plane {
        padding: rem(18) rem(18);
        border-radius: rem(9);
        min-height: rem(65);
        box-shadow: 0 rem(3) 0 0 #00000028;
        width: fit-content;
        max-width: 70%;

        &::before {
            content: "";
            position: absolute;
            top: rem(-10);
            left: 0;
            width: rem(15);
            height: 100%;
            z-index: -1;
            background-color: $color-accent-light-b;
            clip-path: polygon(100% 0%, 100% #{rem(15)}, #{rem(1)} #{rem(15)});
        }

        &.company-left-plane--primary {
            &::before {
                background-color: $color-primary-b-darker;
            }
        }
        &.company-left-plane--secondary {
            &::before {
                background-color: $color-secondary-b-darkest;
            }
        }
        &.company-left-plane--logo {
            background-color: $color-accent-light-b;

            &::before {
                background-color: $color-accent-light-c;
            }
        }
    }

    .company-occupation-plane {
        margin-left: rem(-40);
        margin-bottom: rem(20);
    }

    .company-body {
        &::after {
            content: "";
            display: block;
            clear: both;
        }
    }

    .company-left {
        .company-left-plane {
            float: left;
            clear: left;
            margin-left: rem(-40);
            margin-right: rem(30);
            margin-bottom: rem(20);
        }
    }

    .company-rating-mobile {
        display: none;

        .company-rating-mobile-plane {
            width: auto;
            padding: 0 rem(18);
            border-radius: rem(9);
            height: rem(65);
            box-shadow: 0px rem(3) 0px 0px #00000028;
        }
    }

    .company-card-content {
        // height: rem(1000);
        padding-bottom: rem(25);

        .economic-sectors {
            row-gap: rem(10);
            margin-bottom: rem(30);

            .economic-sector {
                padding: rem(10) rem(20);
                border-radius: rem(50);
                background-color: $color-accent-light-b;
                cursor: pointer;

                &:hover {
                    background-color: $color-accent-light-c;
                }
            }
        }
    }
}

@include media-breakpoint-down(xxl) {
    .company-info {
        .company-info-plane-wrapper {
            gap: rem(25);
            .company-info-plane {
                // border-radius: rem(10);
                max-width: calc(50% - #{rem(20)});
            }
        }
    }
}
@include media-breakpoint-down(xl) {
    .company-card {
        .company-occupation-plane {
            max-width: 60%;
        }
        .company-left-plane {
            max-width: 50%;
        }
    }

    .company-info {
        .company-info-plane-wrapper {
            gap: rem(20);
            .company-info-plane {
                max-width: calc(50% - #{rem(10)});
            }
        }
    }
}
@include media-breakpoint-down(lg) {
    .more-info-button {
        padding-left: rem(55);
        padding-right: rem(55);
    }

    .company-card {
        .company-right {
            .company-right-rating-text {
                text-wrap: auto;
            }

            .company-right-plane {
                padding: 0 rem(15);
                border-radius: rem(8);
                height: rem(55);
            }
        }

        .company-occupation-plane {
            max-width: 60%;
        }

        .company-left {
            .company-left-plane {
                margin-right: rem(20);
            }
        }

        .company-card-content {
            .economic-sectors {
                margin-bottom: rem(20);

                .economic-sector {
                    padding: rem(7) rem(15);
                }
            }
        }
    }

    .company-info {
        .company-info-plane-wrapper {
            gap: rem(15);
            .company-info-plane {
                border-radius: rem(8);
                max-width: calc(50% - #{rem(8)});
            }
        }
    }
}
@include media-breakpoint-down(md) {
    .more-info-button {
        padding-left: rem(40);
        padding-right: rem(40);
    }

    .company-info {
        .company-info-plane-wrapper {
            gap: rem(10);
            .company-info-plane {
                max-width: 100%;
            }
        }
    }

    .company-contact {
        .company-contact-info-wrapper {
            flex-direction: column;

            .company-contact-image {
                width: fit-content;
                max-width: min(60%, #{rem(300)});
            }

            .company-contact-info {
                width: fit-content;
                max-width: 100%;
            }
        }
    }

    .company-info-bottom {
        flex-direction: column;
        align-items: center;

        .company-info-bottom-left {
            align-items: center;

            .company-info-bottom-left-text {
                text-align: center;
            }
        }

        .company-info-bottom-right {
            align-items: center;

            .company-info-bottom-right-content {
                .company-info-bottom-right-content-icon {
                    position: absolute;
                    left: rem(-30);
                    top: 50%;
                    transform: translateY(-50%);
                }
                .company-info-bottom-right-content-text {
                    text-align: center;
                }
            }
        }
    }

    // FULL CHANGE
    .company-card {
        padding: rem(15);
        padding-bottom: rem(35);

        &.more-info {
            .company-card-content {
                padding-bottom: rem(10);
            }

            .company-left-plane--logo {
                .square-logo {
                    max-height: rem(160);

                    img {
                        max-height: rem(160);
                    }
                }
            }
        }

        .company-right {
            display: none;

            position: relative;
            top: unset;
            right: unset;

            width: 100%;
            max-width: 100%;
            justify-content: space-between;
            gap: rem(10);

            margin-bottom: rem(15);
            order: 3;

            .company-right-rating-text {
                text-align: left;
                text-wrap: nowrap;
            }

            .company-right-plane {
                height: rem(50);
                padding: 0 rem(14);

                &::before {
                    display: none;
                }
            }
        }

        .company-occupation-plane {
            margin-left: 0;
            margin-bottom: rem(15);
            max-width: 100%;
            width: 100%;

            &::before {
                display: none;
            }
        }

        .company-rating-mobile {
            display: flex;
            padding-top: rem(10);

            .company-rating-mobile-plane {
                padding: 0 rem(15);
                height: rem(55);
            }
        }

        .company-body {
            display: flex;
            flex-direction: column;

            &::after {
                display: none;
            }
        }

        .company-left {
            display: flex;
            flex-direction: column;
            gap: rem(15);

            .company-left-plane {
                float: none;
                clear: none;

                margin-left: 0;
                margin-right: 0;
                margin-bottom: 0;

                width: 100%;
                max-width: 100%;

                &::before {
                    display: none;
                }
            }
        }

        .company-left-plane--logo {
            display: flex;
            justify-content: center;
            align-items: center;

            .square-logo {
                width: auto;
                height: height;
                max-width: 100%;
                max-height: rem(96);
                aspect-ratio: unset;

                img {
                    width: auto;
                    height: auto;
                    max-width: 100%;
                    max-height: rem(96);
                }
            }
        }

        .company-card-content {
            margin-top: rem(20);
            padding-bottom: 0;

            .economic-sectors {
                justify-content: center;
                gap: rem(8);
                margin-bottom: rem(18);

                .economic-sector {
                    padding: rem(7) rem(12);
                }
            }

            .company-description {
                text-align: center;

                &:not(.text--line-clamp-3) {
                    display: block;
                }
            }
        }

        .more-info-button {
            bottom: rem(-50);
        }
    }
}
@include media-breakpoint-down(sm) {
    .more-info-button {
        padding-left: rem(25);
        padding-right: rem(25);
    }

    .company-card {
        .company-rating-mobile {
            .company-rating-mobile-plane {
                padding: 0 rem(12);
                height: rem(45);
            }
        }
    }

    .company-contact {
        .company-contact-info-wrapper {
            .company-contact-image {
                width: fit-content;
                max-width: 100%;
            }
        }
    }
}
</style>
